// CreaturesDashboard.jsx - Main dashboard component
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Paper,
  Tabs,
  Tab,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { useUnits } from "../../contexts/UnitContext";
import CreatureCard from "./CreatureCard";
import CreaturesByLocationView from "./CreaturesByLocationView";
import useDashboardApi from "../../utils/dashboardApi";

const CreaturesDashboard = ({ dashboardDivesites }) => {
  const { request } = useDashboardApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState({
    allCreatures: [],
    creaturesByLocation: [],
    categories: [],
    totalSightings: 0,
    uniqueCreatures: 0,
    totalCount: 0,
  });

  // Pagination and filtering state
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("sightings");
  const [currentView, setCurrentView] = useState(0); // 0 = creatures, 1 = locations

  const limit = 12;

  // Extract divesite IDs from dashboardDivesites
  const getDivesiteIds = () => {
    if (!dashboardDivesites || !Array.isArray(dashboardDivesites)) {
      return [];
    }
    return dashboardDivesites
      .filter((divesite) => divesite && divesite._id)
      .map((divesite) => divesite._id);
  };

  const fetchCreaturesDashboardData = async () => {
    const divesiteIds = getDivesiteIds();

    if (divesiteIds.length === 0) {
      setDashboardData({
        allCreatures: [],
        creaturesByLocation: [],
        categories: [],
        totalSightings: 0,
        uniqueCreatures: 0,
        totalCount: 0,
      });
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params = {
        page,
        limit,
        searchTerm,
        category: categoryFilter === "all" ? undefined : categoryFilter,
        sortBy,
      };

      // Use the paginated endpoint
      const data = await request(
        "/api/creatures",
        "dashboard/paginated",
        "POST",
        { divesiteIds },
        {},
        params
      );

      setDashboardData(data);
    } catch (err) {
      console.error("Error fetching creatures dashboard data:", err);
      setError(err.response?.data?.message || "Failed to load creatures data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCreaturesDashboardData();
  }, [dashboardDivesites, page, searchTerm, categoryFilter, sortBy]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, categoryFilter, sortBy]);

  const handleViewChange = (event, newValue) => {
    setCurrentView(newValue);
  };



  const renderViewTabs = () => (
    <Paper sx={{ mb: 3 }}>
      <Tabs
        value={currentView}
        onChange={handleViewChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
      >
        <Tab label="By Creatures" />
        <Tab label="By Locations" />
      </Tabs>
    </Paper>
  );

  const renderFiltersAndControls = () => {
    // Only show filters for creatures view
    if (currentView !== 0) return null;

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search creatures..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Category</InputLabel>
              <Select
                value={categoryFilter}
                label="Category"
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {dashboardData.categories?.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortBy}
                label="Sort By"
                onChange={(e) => setSortBy(e.target.value)}
              >
                <MenuItem value="sightings">Most Sightings</MenuItem>
                <MenuItem value="recent">Most Recent</MenuItem>
                <MenuItem value="name">Name A-Z</MenuItem>
                <MenuItem value="locations">Most Locations</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>
    );
  };

  const renderCreaturesView = () => {
    const totalPages = Math.ceil(dashboardData.totalCount / limit);

    return (
      <>
        <Grid container spacing={3}>
          {dashboardData.allCreatures?.map((creature) => (
            <Grid item xs={12} sm={6} md={4} key={creature._id}>
              <CreatureCard
                creature={creature}
                divesites={dashboardDivesites}
              />
            </Grid>
          ))}
        </Grid>

        {totalPages > 1 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        )}

        {dashboardData.allCreatures?.length === 0 && (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <Typography variant="h6" color="textSecondary">
              No creatures found matching your criteria
            </Typography>
          </Box>
        )}
      </>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      );
    }

    const divesiteIds = getDivesiteIds();
    if (divesiteIds.length === 0) {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          No divesites available in your dashboard. Add some divesites to see
          creature data.
        </Alert>
      );
    }

    if (currentView === 0) {
      return renderCreaturesView();
    } else {
      return (
        <CreaturesByLocationView
          creaturesByLocation={dashboardData.creaturesByLocation}
          divesites={dashboardDivesites}
          onRefresh={fetchCreaturesDashboardData}
        />
      );
    }
  };

  return (
    <Box>

      {renderViewTabs()}
      {renderFiltersAndControls()}
      {renderContent()}
    </Box>
  );
};

export default CreaturesDashboard;
